import {
	comparePeriodsEqual,
	getCustomComparePeriodDateKeys,
} from '../../../filters/lib/statisticsComparePresets';
import { expandLineCompareAnchorToSegments, resolveTrendMonthlySpecForQuery } from '../../../filters/lib/statisticsTrendFilters';
import { filterLineComparePeriodYoyByYears, PERIOD_YOY_GRANULARITY, resolveLineComparePeriodSubsetIndices } from '../../../filters/lib/statisticsPeriodYoyHelpers';
import { hasActiveTrendConfig } from '../../pipeline/lib/summarizePrimaryPhaseStatPending';
import {
	buildLineCompareDimensionAxisMetricSeries,
	buildLineCompareDimensionChartData,
	isTrendDimensionAxisMode,
	TREND_AXIS_MODE,
} from '../../../filters/lib/statisticsTrendDimension';
import { buildCurrentStatisticsComparePoint, mapComparesForLens } from '../../../filters/lib/statisticsCompareHelpers';
import { pickStatisticsScopeFields } from '../../../filters/lib/statisticsFilterParams';
import { ORDER_PRIMARY_PHASE_METRICS } from '../../pipeline/lib/orderPrimaryPhaseMetric';
import { normalizeTrendYAxisMetrics, resolvePrimaryTrendYAxisMetric } from './trendYAxisMetrics';
import { CHART_BAR_ORIENTATION } from '../../shared/lib/barOrientation';
import { TREND_CHART_TYPE } from './trendChartConfig';
import {
	buildCustomerFilterCityRows,
	resolveHighlightProvincesFromCustomerFilter,
} from '../../pipeline/lib/orderPrimaryPhaseGeoBuckets';

export const buildTrendChartBundle = ({
	filters = {},
	primaryPhaseStats = null,
	selectedPhase = null,
	fetchedSaleUsers = [],
	fetchedAssistantUsers = [],
	fetchedCustomers = [],
	isStatisticsQueryLoading = false,
	isStatisticsQueryFetching = false,
}) => {
	const trendAxisMode = filters.trendAxisMode ?? TREND_AXIS_MODE.TIME;
	const trendDateField = filters.trendDateField === 'completedAt' ? 'completedAt' : 'createdAt';
	const trendYAxisMetrics = normalizeTrendYAxisMetrics(filters);
	const trendYAxisMetric = resolvePrimaryTrendYAxisMetric(trendYAxisMetrics);
	const trendChartType = filters.trendChartType ?? TREND_CHART_TYPE.BAR;
	const trendLineShape = filters.trendLineShape ?? 'smooth';
	const trendBarOrientation = filters.trendBarOrientation ?? CHART_BAR_ORIENTATION.VERTICAL;
	const trendMetricSeriesTypes = filters.trendMetricSeriesTypes ?? {};
	const trendYearSeriesColors = filters.trendYearSeriesColors ?? {};

	const allOrders = primaryPhaseStats?.orders ?? [];
	const customerFilterIds = filters.customers ?? [];
	const highlightProvinceNames = resolveHighlightProvincesFromCustomerFilter(customerFilterIds, {
		customers: fetchedCustomers,
		orders: allOrders,
	});
	const highlightAnchorCityRows = buildCustomerFilterCityRows(customerFilterIds, {
		customers: fetchedCustomers,
		orders: allOrders,
	});

	const trendsForLens = (() => {
		const mapped = mapComparesForLens(primaryPhaseStats?.compares).filter(
			(item) => item.kind === 'anchor' || item.kind === 'custom',
		);
		const filterPeriods = filters.customComparePeriods ?? [];
		const anchorFilter = filters.trendAnchor;

		const labeled = mapped.map((item) => {
			if (item.kind === 'anchor') {
				const anchorSegments = anchorFilter ? expandLineCompareAnchorToSegments(anchorFilter) : null;
				if (anchorSegments?.length) {
					const matchedSegment = anchorSegments.find((segment) =>
						comparePeriodsEqual(
							{ startDate: segment.startDate, endDate: segment.endDate },
							{ startDate: item.periodStartDate, endDate: item.periodEndDate },
						),
					);
					if (matchedSegment?.label?.trim()) {
						return { ...item, title: matchedSegment.label.trim() };
					}
				}
				const anchorLabel = anchorFilter?.label?.trim();
				return anchorLabel ? { ...item, title: anchorLabel } : item;
			}
			if (item.kind !== 'custom' || !filterPeriods.length) return item;
			const matched = filterPeriods.find((period) =>
				comparePeriodsEqual(
					{ startDate: period?.startDate, endDate: period?.endDate },
					{ startDate: item.periodStartDate, endDate: item.periodEndDate },
				),
			);
			const filterLabel = matched?.label?.trim();
			return filterLabel ? { ...item, title: filterLabel } : item;
		});

		const hasLinePoints = labeled.some((item) => (item.kind === 'anchor' || item.kind === 'custom') && item.periodStartDate);
		if (hasLinePoints) return labeled;
		const current = buildCurrentStatisticsComparePoint(primaryPhaseStats);
		return current ? [current] : labeled;
	})();

	const trendMonthlySpec = resolveTrendMonthlySpecForQuery(filters);
	const trendYearMonthsMode = Boolean(trendMonthlySpec);
	const trendPeriodYoy = (() => {
		if (!trendYearMonthsMode) return null;
		const raw = primaryPhaseStats?.trendPeriodYoy ?? null;
		if (!raw || !trendMonthlySpec?.years?.length) return raw;
		return filterLineComparePeriodYoyByYears(raw, trendMonthlySpec.years);
	})();

	const trendChartLoading = (() => {
		// 尚无主结果：只要还在查 / 等待 batch，就保持骨架（避免标题先出、图区变白）
		if (!primaryPhaseStats && (isStatisticsQueryLoading || isStatisticsQueryFetching)) return true;
		const hasLineCompare = hasActiveTrendConfig(filters);
		if (hasLineCompare && isStatisticsQueryFetching) return true;
		if (trendYearMonthsMode) {
			const apiYears = primaryPhaseStats?.trendPeriodYoy?.years ?? [];
			const specYears = trendMonthlySpec?.years ?? [];
			const yoyReady =
				specYears.length > 0 &&
				apiYears.length === specYears.length &&
				specYears.every((year, index) => year === apiYears[index]);
			if (!yoyReady && (isStatisticsQueryLoading || isStatisticsQueryFetching)) return true;
			if (!isStatisticsQueryFetching) return false;
			return !yoyReady;
		}
		return false;
	})();

	const trendMonthIndices = resolveLineComparePeriodSubsetIndices(
		trendMonthlySpec?.granularity === PERIOD_YOY_GRANULARITY.QUARTER ? null : trendMonthlySpec?.months,
		trendPeriodYoy?.months,
		12,
	);

	const trendGranularity = trendMonthlySpec?.granularity ?? PERIOD_YOY_GRANULARITY.MONTH;
	const trendDualFullPeriod = Boolean(trendMonthlySpec?.dualFullPeriod);

	const trendPeriodIndices = (() => {
		if (trendGranularity === PERIOD_YOY_GRANULARITY.QUARTER) {
			return resolveLineComparePeriodSubsetIndices(trendMonthlySpec?.quarters, trendPeriodYoy?.quarters, 4);
		}
		return trendMonthIndices;
	})();

	const trendDimensionAxisBundle = (() => {
		if (!isTrendDimensionAxisMode(trendAxisMode)) {
			return { count: null, totalPrice: null, seriesByMetric: null, fieldLabel: '', axisMode: trendAxisMode };
		}

		const rawChartData = buildLineCompareDimensionChartData({
			orders: allOrders,
			field: trendAxisMode,
			filters,
			saleUsers: fetchedSaleUsers,
			assistantUsers: fetchedAssistantUsers,
			customerOptions: fetchedCustomers.map((customer) => ({
				value: customer._id,
				label: customer.name || '未知客户',
			})),
			selectedPhase,
		});

		return {
			count: buildLineCompareDimensionAxisMetricSeries(rawChartData, ORDER_PRIMARY_PHASE_METRICS.COUNT),
			totalPrice: buildLineCompareDimensionAxisMetricSeries(rawChartData, ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE),
			seriesByMetric: Object.fromEntries(
				trendYAxisMetrics.map((metric) => [metric, buildLineCompareDimensionAxisMetricSeries(rawChartData, metric)]),
			),
			fieldLabel: rawChartData.fieldLabel,
			axisMode: trendAxisMode,
		};
	})();

	const trendChartKey = (() => {
		const spec = resolveTrendMonthlySpecForQuery(filters);
		if (spec?.years?.length) {
			const monthKey = spec.months?.length ? spec.months.join('-') : 'all-months';
			const quarterKey = spec.quarters?.length ? spec.quarters.join('-') : '';
			return `ym-${spec.years.join(',')}-${monthKey}${quarterKey ? `-${quarterKey}` : ''}`;
		}
		const periodKeys = getCustomComparePeriodDateKeys(filters.customComparePeriods);
		const anchorKey = filters.trendAnchor
			? `${filters.trendAnchor.startDate}|${filters.trendAnchor.endDate}`
			: '';
		return periodKeys.length || anchorKey ? `p-${anchorKey}-${periodKeys.join('|')}` : 'none';
	})();

	const trendScopeKey = (() => {
		const { startDate, endDate, completedStartDate, completedEndDate, compareYoy, compareMom, ...scopeRest } =
			pickStatisticsScopeFields(filters);
		return JSON.stringify(scopeRest);
	})();

	const animationKey = `trend-${selectedPhase ?? 'all'}-${trendChartKey}-${trendScopeKey}-${trendAxisMode}-${trendDateField}-${trendYAxisMetrics.join(',')}-${trendChartType}-${JSON.stringify(trendMetricSeriesTypes)}-${trendLineShape}-${trendBarOrientation}`;

	return {
		compares: trendsForLens,
		orders: allOrders,
		highlightProvinceNames,
		highlightAnchorCityRows,
		trendYearMonthsMode,
		trendDualFullPeriod,
		trendGranularity,
		trendMonthIndices,
		trendPeriodIndices,
		trendPeriodYoy,
		trendAxisMode,
		trendDateField,
		trendYAxisMetric,
		trendYAxisMetrics,
		trendChartType,
		trendLineShape,
		trendBarOrientation,
		trendMetricSeriesTypes,
		trendYearSeriesColors,
		trendDimensionCountSeries: trendDimensionAxisBundle.count,
		trendDimensionTotalPriceSeries: trendDimensionAxisBundle.totalPrice,
		trendDimensionSeriesByMetric: trendDimensionAxisBundle.seriesByMetric,
		trendDimensionFieldLabel: trendDimensionAxisBundle.fieldLabel,
		trendChartLoading,
		trendChartKey,
		trendScopeKey,
		animationKey,
	};
};
